<template>
	<template v-if="showMe">
		<it-link v-if="isNav(item)" :to="item.link" class="menu-item" @mouseenter="hoverOnItem" :confirmed="needConfirm" @click="executeNavHandler" v-bind="targetAttribute" ref="itLink">
			<sym-icon v-if="hasIcon" :name="item.icon" class="menu-fi"></sym-icon>
			<span v-else class="fi menu-fi"> </span>
			<span class="mi mi-label" :style="extraStyles">{{ item.title }} <span v-if="hasBadge" class="menuBadge">{{ item.badge }}</span></span>
		</it-link>
		<a v-else-if="isExecute(item)" :href="'#'+(item.name??'')" class="menu-item action-item" @mouseenter="hoverOnItem" @click.prevent="executeAction">
			<sym-icon v-if="hasIcon" :name="item.icon" class="menu-fi"></sym-icon>
			<span v-else class="fi menu-fi"> </span>
			<span class="mi mi-label" :style="extraStyles">{{ item.title }} <span v-if="hasBadge" class="menuBadge">{{ item.badge }}</span></span>
		</a>
		<div v-else-if="isActive(item)" class="menu-item active-item" @mouseenter="hoverOnItem">
			<sym-icon v-if="hasIcon" :name="item.icon" class="menu-fi"></sym-icon>
			<span v-else class="fi menu-fi"> </span>
			<span class="mi mi-label" :style="extraStyles">{{ item.title }} <span v-if="hasBadge" class="menuBadge">{{ item.badge }}</span></span>
		</div>
		<div v-else-if="isOption(item)" class="menu-item option-item" @mouseenter="hoverOnItem">
			<sym-icon v-if="hasIcon" :name="item.icon" class="menu-fi"></sym-icon>
			<span v-else class="fi menu-fi"> </span>
			<span class="mi mi-label" :style="extraStyles">{{ item.title }}<a-switch v-model="option" class="menu-option"></a-switch></span>
		</div>
		<div v-else-if="isHelp(item)" class="menu-item help-item" @mouseenter="hoverOnItem">
			<sym-icon v-if="hasIcon" :name="item.icon" class="menu-fi"></sym-icon>
			<span v-else class="fi menu-fi"> </span>
			<span class="mi mi-label" :style="extraStyles">{{ item.title }}</span>
		</div>
		<div v-else-if="isHtml(item)" class="menu-item html-item" @mouseenter="hoverOnItem">
			<sym-icon v-if="hasIcon" :name="item.icon" class="menu-fi"></sym-icon>
			<span v-else class="fi menu-fi"> </span>
			<span class="mi mi-label" :style="extraStyles" v-html="item.body"></span>
		</div>
		<div v-else-if="item.type === 'separator'" class="separator-item" :class="{ 'separator-item-with-title':hasTitle }" @mouseenter="hoverOnItem">{{ item.title }}</div>
		<side-bar-sub-menu v-else-if="item.type === 'menu'" :menu="item"></side-bar-sub-menu>
		<confirm-dialog v-if="showConfirmMessage" :message="item.confirm" :icon="item.confirmIcon ?? item.icon" :icon-color="item.confirmIconColor ?? item.icon"
						:yes-label="item.confirmYes" :no-label="item.confirmNo" @yes="confirmYes" @no="confirmNo"></confirm-dialog>
	</template>
</template>

<script setup lang="ts">
import {type MenuItem, isNav, hasChildren, isOption, isHelp, isAction, ActionItem, ExecuteItem, isActive, isExecute, isHtml, OnClickHandler} from "@t/ui/Menu";
import {computed, ref} from "vue";
import SymIcon from "@fd/helpers/SymIcon.vue";
import ItLink from "@/router/ItLink.vue";
import SideBarSubMenu from "@/ui/mouse/SideBarSubMenu.vue";
import ASwitch from "@fd/components/ASwitch.vue";
import {chainContext} from "@/ui/paneltree/PanelTree.ts";
import {injectEnv} from "@fd/Environment.ts";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import ConfirmDialog from "@/dialog/ConfirmDialog.vue";

const props = defineProps<{
	item: MenuItem
}>();

const showMe = computed(() => !props.item.hidden);
const hasTitle = computed(() => !!props.item.title);
const hasIcon = computed(() => !!props.item.icon);
const hasBadge = computed(() => !!props.item.badge);
const itLink = ref(null);

const navStore = useNavigationStore();
const env = injectEnv();
const chains = chainContext();

const targetAttribute = computed<object>(() => {
	if (isNav(props.item) && props.item.target) return { target: props.item.target };
});

const confirmDialogOpen = ref<boolean>(false);
const needConfirm = computed<boolean>(() => isAction(props.item) && !!props.item.confirm);
const showConfirmMessage = computed<boolean>(() => {
	return confirmDialogOpen.value && needConfirm.value;
});

const option = computed<boolean>({
	get():boolean {
		return navStore.getOption(props.item.name);
	},

	set(v:boolean):void {
		navStore.setOption(props.item.name, v);
	}
});

const extraStyles = computed<string|null>(() => {
	if (props.item.style) return props.item.style;
	return null;
});

function hoverOnItem(e: MouseEvent) {
	chains.closePanels(false);
}

const JS_SCHEME = "javascript:";

function executeOnClick() {
	const mi = props.item as ExecuteItem;
	if (typeof mi.onclick === "function") {
		return mi.onclick(env, props.item);
	} else if (typeof mi.onclick === "string") {
		let oc = mi.onclick;
		if (oc.startsWith(JS_SCHEME)) oc = oc.substring(JS_SCHEME.length);
		const clickFunc = new Function("env", "item", oc) as OnClickHandler;
		return clickFunc(env, props.item);
	} else {
		if (isExecute(mi)) {
			console.warn("Action menu item doesn't have onclick handler");
		} else return true;
	}
}

function afterConfirmNavAction() {
	const retval = executeOnClick();
	if (typeof retval !== "boolean" || !!retval) {
		// simulate it-link behavior
		if (itLink.value) itLink.value.navigate();
	}
}
function afterConfirmExecuteAction() {
	executeOnClick();
}


let afterConfirmAction:()=>void;

function executeNavHandler(e?: MouseEvent) {
	if (needConfirm.value) {
		e.preventDefault();
		confirmDialogOpen.value = true;
		afterConfirmAction = afterConfirmNavAction;
	} else {
		const retval = executeOnClick();	// execute onclick if exists (false will cancel navigation)
		if (typeof retval === "boolean" && !retval) e.preventDefault();	// cancel click
		return retval;
	}
}

function executeAction(e?: MouseEvent) {
	if (needConfirm.value) {
		e.preventDefault();
		confirmDialogOpen.value = true;
		afterConfirmAction = afterConfirmExecuteAction;
	} else {
		return executeOnClick();
	}
}

function confirmYes() {
	console.log("YES called");
	confirmDialogOpen.value = false;
	afterConfirmAction();
}

function confirmNo() {
	console.log("NO called");
	confirmDialogOpen.value = false;
}

</script>

<style>
.option-item {
	--input-height: 18px;
}
.menu-option {
	float:right;
}
.help-item .mi-label {
	font-size: var(--font-size-s);
	color: var(--fg);
}
</style>
